// ChartVisualization.tsx - Component for displaying Chart.js visualizations

import React, { useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Bar, Line, Doughnut, Pie, Radar } from "react-chartjs-2";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { Visualization } from "../../types/table";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ChartVisualizationProps {
  visualization: Visualization;
  className?: string;
}

const ChartVisualization: React.FC<ChartVisualizationProps> = ({
  visualization,
  className = "",
}) => {
  const { type, data, options, title } = visualization;
  const chartRef = useRef<any>(null);

  // Default options with dark blue theme
  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
        display: true,
      },
      title: {
        display: false, // Title is displayed separately
        text: title,
      },
    },
    ...options,
  };

  // Function to download chart as PNG
  const handleDownload = () => {
    if (chartRef.current) {
      const chart = chartRef.current;
      const base64Image = chart.toBase64Image();

      // Create download link
      const link = document.createElement("a");
      link.href = base64Image;
      link.download = `${title.replace(/\s+/g, "_")}_chart.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Render the appropriate chart type
  const renderChart = () => {
    switch (type) {
      case "bar":
        return <Bar ref={chartRef} data={data} options={defaultOptions} />;
      case "line":
        return <Line ref={chartRef} data={data} options={defaultOptions} />;
      case "doughnut":
        return <Doughnut ref={chartRef} data={data} options={defaultOptions} />;
      case "pie":
        return <Pie ref={chartRef} data={data} options={defaultOptions} />;
      case "radar":
        return <Radar ref={chartRef} data={data} options={defaultOptions} />;
      default:
        return (
          <div className="text-center text-gray-500 py-8">
            Unsupported chart type: {type}
          </div>
        );
    }
  };

  return (
    <div className={`${className}`}>
      {title && (
        <div
          className="flex items-center justify-between mb-3 text-white px-3 py-2 rounded"
          style={{ backgroundColor: "#0f3557" }}
        >
          <h3 className="text-sm font-semibold">{title}</h3>
          <button
            onClick={handleDownload}
            className="flex items-center gap-1 px-2 py-1 text-xs font-medium bg-white bg-opacity-20 hover:bg-opacity-30 rounded transition-colors"
            title="Download as PNG"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            PNG
          </button>
        </div>
      )}
      <div style={{ height: "300px", position: "relative" }}>
        {renderChart()}
      </div>
    </div>
  );
};

export default ChartVisualization;
